import React, { useContext, useState } from 'react';
import {
  Accordions,
  AccordionsItem,
  AccordionsItemHeader,
  AccordionsItemSection,
  Badge,
  CondensedHeader,
  Flashy,
  Gutter,
  Icon,
  Layout,
  LayoutItem,
  ToastUtil,
} from '@axiom/ui';

import type { ExperienceSectionStateType } from './ExperienceEditContext';
import { ExperienceEditContext } from './ExperienceEditContext';
import { ExperienceEditDataLossWarningModal } from './ExperienceEditDataLossWarningModal';
import { ExperienceEditSectionKeys } from './experience-edit-util';

export type ExperienceEditSectionProps = {
  children: React.ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  isComplete: boolean;
  name: keyof ExperienceSectionStateType;
  title: string;
};

export const ExperienceEditSection = ({
  children,
  expandable = false,
  expanded = false,
  isComplete,
  name,
  title,
}: ExperienceEditSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { accordionState, setAccordionState, dirtyFormState } = useContext(
    ExperienceEditContext
  );

  const toggleAccordion = (value: boolean) => {
    const nextState: ExperienceSectionStateType = {
      COMPANY_DETAILS: accordionState.COMPANY_DETAILS,
      WORK_DETAILS: accordionState.WORK_DETAILS,
      FOCUS_AREAS: accordionState.FOCUS_AREAS,
      SKILLS: accordionState.SKILLS,
    };
    nextState[name] = value;

    setAccordionState(nextState);
  };

  const isSectionEdited = (key: keyof ExperienceSectionStateType) =>
    dirtyFormState[key] && accordionState[key];

  const shouldModalOpen = (isAccordionExpanding: boolean) =>
    isAccordionExpanding &&
    Object.values(ExperienceEditSectionKeys).some(key => isSectionEdited(key));

  return (
    <>
      <Accordions name={`${name}_EXPERIENCE_EDIT_SECTION`}>
        <AccordionsItem name={name} expanded={expanded}>
          <AccordionsItemHeader
            name={`${name}_ACCORDION_HEADER`}
            disabled={!expandable || isSectionEdited(name)}
            onDisabledToggle={() => {
              if (expandable) return;

              ToastUtil.add({
                dismissible: 10,
                name: 'OPEN_NOT_ALLOWED',
                type: 'error',
                children: (
                  <>
                    Please complete and save the previous section(s) before
                    moving on
                  </>
                ),
              });
            }}
            onToggle={value => {
              toggleAccordion(value);

              if (shouldModalOpen(value)) {
                setIsModalOpen(true);
              }
            }}
          >
            <Layout position="space-between middle">
              <LayoutItem>
                <CondensedHeader name={`${name}_TITLE`}>
                  {title}
                </CondensedHeader>
              </LayoutItem>
              <LayoutItem position="center">
                <Gutter right="16px">
                  {isComplete ? (
                    <Flashy color="controlSuccessPrimary">
                      <Layout name={`${name}_COMPLETE_ICON`}>
                        <Icon name="circle-check-filled" />
                      </Layout>
                    </Flashy>
                  ) : (
                    <Badge background="gray" name={`${name}_BADGE`}>
                      TO DO
                    </Badge>
                  )}
                </Gutter>
              </LayoutItem>
            </Layout>
          </AccordionsItemHeader>
          <AccordionsItemSection>
            <Gutter top="24px">{children}</Gutter>
          </AccordionsItemSection>
        </AccordionsItem>
      </Accordions>
      {isModalOpen ? (
        <ExperienceEditDataLossWarningModal
          onClose={() => {
            setIsModalOpen(false);
            toggleAccordion(false);
          }}
          onContinue={() => {
            setIsModalOpen(false);
            toggleAccordion(true);
          }}
        />
      ) : null}
    </>
  );
};
